import { ApiErrorBody } from "@/contracts/common";
import { buildPath, type Endpoint, type EndpointInput, type EndpointOutput } from "@/contracts/endpoint";
import { collectFiles } from "./uploads";

/**
 * VITE_API_MODE=mock answers every call from the in-memory handlers in src/api/mock (lazy-loaded, so a live
 * build never ships them); "live" calls VITE_API_URL with the staff cookie (`credentials: "include"`).
 * `npm run dev` defaults to mock; a build refuses to run without an explicit mode (vite.config.ts).
 */
export const API_MODE: "mock" | "live" = import.meta.env.VITE_API_MODE === "live" ? "live" : "mock";
const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8787";
/** Client web app origin (VITE_WEB_URL): back-office links into it, e.g. TD-Console « Ouvrir le compte ». */
export const WEB_URL = (import.meta.env.VITE_WEB_URL || "http://localhost:3000").replace(/\/+$/, "");
/** Mock data outside `vite dev` = a demo deployment: the shell shows the « Démo » banner. */
export const IS_DEMO = API_MODE === "mock" && !import.meta.env.DEV;

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public fields?: Record<string, string>,
    public retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const OFFLINE_MESSAGE = "Connexion perdue : vous pouvez lire cette page, mais les actions attendront le retour du réseau.";
export const SESSION_EXPIRED_MESSAGE = "Session expirée. Merci de vous reconnecter.";
const UNKNOWN_MESSAGE = "Une erreur est survenue.";

/** Fired on any 401 from an authenticated endpoint; the session provider listens and sends to /connexion?expire=1. */
export const SESSION_EXPIRED_EVENT = "melrys-admin:session-expired";

type AnyInput = { params?: Record<string, unknown>; query?: Record<string, unknown>; body?: unknown };

export async function call<E extends Endpoint>(ep: E, input?: EndpointInput<E>): Promise<EndpointOutput<E>> {
  const { params, query, body } = (input ?? {}) as AnyInput;
  const parsedBody = ep.body && body !== undefined ? ep.body.parse(body) : undefined;
  const parsedQuery = ep.query && query !== undefined ? (ep.query.parse(query) as Record<string, unknown>) : undefined;
  try {
    const raw =
      API_MODE === "mock"
        ? await (await import("./mock")).handle(ep, { params: params ?? {}, query: parsedQuery ?? {}, body: parsedBody })
        : await fetchLive(ep, params, parsedQuery, parsedBody, body);
    const parsed = ep.response.safeParse(raw);
    if (!parsed.success) {
      console.error(
        `[contract] ${ep.id} (${ep.method} ${ep.path}): response does not match the contract`,
        parsed.error.issues
          .slice(0, 5)
          .map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`)
          .join(" · "),
      );
      throw parsed.error;
    }
    return parsed.data as EndpointOutput<E>;
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 401 && ep.auth !== false) window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      throw err;
    }
    if (err instanceof TypeError) throw new ApiError(0, "network", OFFLINE_MESSAGE);
    throw err;
  }
}

async function fetchLive(ep: Endpoint, params?: Record<string, unknown>, query?: Record<string, unknown>, body?: unknown, rawBody?: unknown) {
  const url = new URL(buildPath(ep.path, params), API_URL);
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
  const init: RequestInit = { method: ep.method, credentials: "include" };
  // Upload metadata tied to a browser File (./uploads.ts) → multipart: `payload` (the parsed JSON body) + the files.
  // The files are looked up on the raw body: the contract's parse returns new objects.
  const parts = body !== undefined ? collectFiles(rawBody) : [];
  if (parts.length) {
    const form = new FormData();
    form.set("payload", JSON.stringify(body));
    for (const p of parts) form.append(p.field, p.file, p.name);
    init.body = form;
  } else if (body !== undefined) Object.assign(init, { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  const res = await fetch(url, init);
  const json: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const parsed = ApiErrorBody.safeParse(json);
    if (parsed.success) {
      const e = parsed.data;
      throw new ApiError(res.status, e.code, e.message, e.fields, e.retryAfterSeconds);
    }
    throw new ApiError(res.status, "unknown", UNKNOWN_MESSAGE);
  }
  return json;
}
